import { IconPhotoFilled } from '@tabler/icons-react';
import { match } from 'ts-pattern';
import type { PlaywrightOperationOptions } from '~/types';

const _baseActions = [
  'expect',
  'click',
  'dblclick',
  'hover',
  'fill',
  'press',
  'pressSequentially',
  'selectOption',
  'type',
  'check',
  'uncheck',
  'clear',
  'waitForTimeout',
  'navigate',
  'focus',
  'scroll',
  // 'waitForSelector',
  // 'dispatchEvent',
  'goBack',
  'goForward',
  'reload',
  'dragTo',
  'swipe',
  'goto',
  'endTest',
] as const;
export type BaseAction = (typeof _baseActions)[number];

const getActionDisplay = (
  action: BaseAction,
  options?: PlaywrightOperationOptions,
): { display: string; suffix?: string; icon?: React.ReactNode } => {
  return match(action)
    .with('expect', () => ({
      display: 'Expect',
      icon: (
        <IconPhotoFilled
          color="orange"
          size={15}
        />
      ),
    }))
    .with('click', () => ({
      display:
        options?.button === 'right' ? 'Right click'
        : options?.clickCount === 3 ? 'Triple click'
        : 'Click',
      suffix: 'on',
    }))
    .with('dblclick', () => ({ display: 'Double click', suffix: 'on' }))
    .with('hover', () => ({ display: 'Hover', suffix: 'over' }))
    .with('fill', () => ({ display: 'Fill' }))
    .with('type', () => ({ display: 'Type', suffix: 'on keyboard' }))
    .with('press', () => ({ display: 'Press', suffix: 'on keyboard' }))
    .with('pressSequentially', () => ({ display: 'Press sequentially' }))
    .with('selectOption', () => ({ display: 'Select an option', suffix: 'from' }))
    .with('check', () => ({ display: 'Check' }))
    .with('uncheck', () => ({ display: 'Uncheck' }))
    .with('clear', () => ({ display: 'Clear' }))
    .with('navigate', () => ({ display: 'Navigate', suffix: 'to' }))
    .with('focus', () => ({ display: 'Focus', suffix: 'on' }))
    .with('waitForTimeout', () => ({ display: 'Wait for' }))
    .with('goBack', () => ({ display: 'Navigate back' }))
    .with('goForward', () => ({ display: 'Navigate forward' }))
    .with('goto', () => ({ display: 'Navigate to' }))
    .with('reload', () => ({ display: 'Reload Page' }))
    .with('dragTo', () => ({ display: 'Drag' }))
    .with('swipe', () => ({ display: 'Swipe', suffix: 'from' }))
    .with('scroll', () => ({ display: 'Scroll' }))
    .with('endTest', () => ({ display: 'End test', suffix: 'with' }))
    .exhaustive();
};

export default function ActionPart({ action, options }: { action?: BaseAction; options?: PlaywrightOperationOptions }) {
  const actionDisplayInfo = action ? getActionDisplay(action, options) : { display: '', suffix: '', icon: undefined };
  return (
    <div className="flex items-center space-x-1">
      {!!actionDisplayInfo.icon && <div>{actionDisplayInfo.icon}</div>}
      <div>{[actionDisplayInfo.display, actionDisplayInfo.suffix].filter(Boolean).join(' ')}</div>
    </div>
  );
}
